import Link from "next/link";
import { getApiErrorMessage } from "queries/lib/rocky-api-error-handler";
import React, { useEffect, useState } from "react";
import { type QueryKey, useQueryClient } from "react-query";
import { Skeleton } from "ui/components/primitives/skeleton";
import { cn } from "ui/lib/utils";

type Action = { label: string; onClick?: () => void; href?: string };

type LoadStateProps = {
  loading: boolean;
  error?: unknown;
  /** Loaded, but there is nothing to show. */
  empty?: boolean;
  /** What failed to load, for the error panel: "Couldn't load badges." */
  what: string;
  /** From the slow state it asks to cancel the stuck request (react-query's `cancelRefetch`). */
  onRetry?: (options?: { cancelRefetch?: boolean }) => void;
  /**
   * The react-query key behind `loading`. A query with no data ignores
   * `cancelRefetch` and hands back its stuck promise, so the slow Retry
   * cancels the key first.
   */
  queryKey?: QueryKey;
  /** Shown while loading; defaults to a few skeleton rows. */
  skeleton?: React.ReactNode;
  emptyMessage?: string;
  emptyAction?: Action;
  /** After this long still loading, offer Retry instead of waiting forever. */
  timeoutMs?: number;
  className?: string;
  children?: React.ReactNode;
};

const ActionButton = ({ label, onClick, href }: Action) => {
  const cls =
    "mt-3 inline-flex min-h-11 items-center rounded-lg bg-white/[0.08] px-4 text-[0.9375rem] text-white/90 hover:bg-white/[0.12] active:bg-white/[0.12] md:min-h-9 md:text-sm";
  return href ? (
    <Link href={href} className={cls}>
      {label}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={cls}>
      {label}
    </button>
  );
};

export const LoadStateSkeleton = ({ rows = 5 }: { rows?: number }) => (
  <div className="space-y-2 p-4">
    {Array.from({ length: rows }, (_, n) => (
      <Skeleton key={n} className="h-12 rounded-xl bg-white/[0.04]" />
    ))}
  </div>
);

/**
 * Loading, empty and error states for a query. Once loaded with content it
 * renders `children` untouched, so a page's loaded layout never changes.
 */
export const LoadState = ({
  loading,
  error,
  empty,
  what,
  onRetry,
  skeleton,
  emptyMessage,
  emptyAction,
  timeoutMs = 15000,
  queryKey,
  className,
  children,
}: LoadStateProps) => {
  const queryClient = useQueryClient();
  const [slow, setSlow] = useState(false);
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    setSlow(false);
    if (!loading) return;
    const timer = setTimeout(() => setSlow(true), timeoutMs);
    return () => clearTimeout(timer);
  }, [loading, timeoutMs, attempt]);

  const retry = () => (onRetry ? onRetry() : window.location.reload());
  const retrySlow = () => {
    setAttempt((n) => n + 1);
    if (!onRetry) return window.location.reload();
    // Synchronous up to here: the cancel and the new fetch land in one
    // render, so the reverted idle state between them never paints.
    if (queryKey) void queryClient.cancelQueries(queryKey);
    onRetry({ cancelRefetch: true });
  };

  if (error && !loading) {
    return (
      <div role="alert" className={cn("px-4 py-10 text-center", className)}>
        <p className="text-[0.9375rem] text-white/80">{what}</p>
        <p className="mt-1 text-sm text-faint">
          {getApiErrorMessage(error)}
        </p>
        <ActionButton label="Retry" onClick={retry} />
      </div>
    );
  }

  if (loading) {
    return (
      <div aria-busy="true" className={className}>
        <span className="sr-only">Loading</span>
        {slow ? (
          <div role="status" className="px-4 py-10 text-center">
            <p className="text-[0.9375rem] text-white/70">
              This is taking longer than usual.
            </p>
            <ActionButton label="Retry" onClick={retrySlow} />
          </div>
        ) : (
          (skeleton ?? <LoadStateSkeleton />)
        )}
      </div>
    );
  }

  if (empty) {
    return (
      <div className={cn("px-4 py-10 text-center", className)}>
        <p className="text-[0.9375rem] text-white/60">
          {emptyMessage ?? "Nothing here yet."}
        </p>
        {emptyAction ? <ActionButton {...emptyAction} /> : null}
      </div>
    );
  }

  return <>{children}</>;
};
